import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  RemoveFeedFromExperienceMutation,
  RemoveFeedFromExperienceMutationVariables,
} from './useRemoveFeed.generated';

// Removes any connected calendar from the experience — a Google/ICS source or an
// experience-backed feed. The server resolves the feed by id (source.id or
// feedExperience.id), so the caller passes the ExperienceFeed id regardless of kind.
const query = gql`
  mutation RemoveFeedFromExperience($input: RemoveFeedFromExperienceInput!) {
    removeFeedFromExperience(input: $input)
  }
`;

export const useRemoveFeed = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();

  const { mutate, ...rest } = useMutation<
    RemoveFeedFromExperienceMutation,
    QueryError,
    RemoveFeedFromExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        RemoveFeedFromExperienceMutation,
        RemoveFeedFromExperienceMutationVariables
      >(query, variables),
    // Refresh experience.feeds and drop the removed feed's events (the events key is
    // nested under the experience key, so one invalidation covers both).
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
      }),
  });

  const removeFeed = (
    feedId: string,
    options?: MutateOptions<
      RemoveFeedFromExperienceMutation,
      QueryError,
      RemoveFeedFromExperienceMutationVariables
    >
  ) => mutate({ input: { experienceId: experience.id, feedId } }, options);

  return { removeFeed, ...rest };
};
